/**
 * B3 · Panel de PASARELAS: conciliación, drenaje y cola de webhooks.
 *
 * Es la pieza que convierte el kill-switch de tres estados en algo operable.
 * Antes no había forma de responder a la única pregunta que importa cuando una
 * pasarela falla: **¿cuánto dinero hay a medio camino?** Sin ese dato, apagar
 * es a ciegas, y apagar a ciegas es perder cobros.
 *
 * Lo que enseña, en este orden:
 *  1. estado (`active`/`draining`/`off`) y pagos EN VUELO con su antigüedad —
 *     «Stripe · en retirada · 3 pagos en vuelo · el más antiguo hace 12 min»;
 *  2. lo cobrado en 30 días y la COMISIÓN del proveedor (la diferencia que la
 *     conciliación siempre daba y no se podía explicar);
 *  3. la cola de eventos que espera a un humano, con «resolver» auditado.
 *
 * `draining` es el valor operativo del apagado: corta cobros nuevos y deja que
 * lo que ya está en vuelo aterrice. `off` solo tras el drenaje — y ni siquiera
 * entonces se descarta un webhook: se persiste para liquidarlo a mano.
 */
import { type Component, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { AlertTriangle, PlugZap } from "lucide-solid";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { showToast } from "@/components/ui/toast";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { Money } from "./money";
import {
  type GatewayState,
  useCashForecast,
  useGatewayPanel,
  useSetGatewayState,
  useSettleWebhookEvent,
  useWebhookEvents,
} from "../api";

const stateClass = (state: GatewayState): string => {
  if (state === "draining") {
    return "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200";
  }
  if (state === "off") {
    return "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200";
  }
  return "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200";
};

const MS_PER_MINUTE = 60_000;

/** «hace 12 min» — la antigüedad del pago en vuelo más viejo. */
const minutesAgo = (iso: string): number =>
  Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / MS_PER_MINUTE));

const todayIso = (): string => new Date().toISOString().slice(0, 10);
const inNinetyDaysIso = (): string => {
  const d = new Date();
  d.setDate(d.getDate() + 90);
  return d.toISOString().slice(0, 10);
};

export const GatewayPanel: Component = () => {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();
  const panel = useGatewayPanel(activeOrgId);
  const events = useWebhookEvents(activeOrgId, () => undefined);
  const forecast = useCashForecast(activeOrgId, todayIso, inNinetyDaysIso);
  const setState = useSetGatewayState();
  const settle = useSettleWebhookEvent();
  const [note, setNote] = createSignal("");
  const [settling, setSettling] = createSignal<string | undefined>();

  const onSetState = (key: string, state: GatewayState) => {
    setState.mutate(
      { org: activeOrgId(), key, state },
      {
        onSuccess: (res) => {
          // El nº de pagos en vuelo al apagar es EL dato del incidente.
          const inFlight = res.inFlightCount;
          showToast(
            inFlight > 0
              ? t(msg`Saved. ${inFlight} payment(s) still in flight will still settle.`)
              : t(msg`Saved. Nothing is in flight.`),
            "success",
          );
        },
        onError: (e) => showToast(e.message, "error"),
      },
    );
  };

  const onSettle = (id: string) => {
    if (!note().trim()) {
      showToast(t(msg`Explain how it was resolved (it goes in the audit trail)`), "error");
      return;
    }
    settle.mutate(
      { org: activeOrgId(), id, note: note().trim() },
      {
        onSuccess: () => {
          showToast(t(msg`Event resolved`), "success");
          setSettling(undefined);
          setNote("");
        },
        onError: (e) => showToast(e.message, "error"),
      },
    );
  };

  return (
    <div class="space-y-4" data-testid="gateway-panel">
      {/* B4 · G5 — el cuadro de caja del dueño, arriba del todo: es lo que
          justifica el descuento del prepago. */}
      <div class="rounded-xl border border-border p-4">
        <h2 class="mb-3 font-semibold">{t(msg`Cash position (next 90 days)`)}</h2>
        <Show when={!forecast.isLoading} fallback={<Skeleton class="h-16 w-full" />}>
          <dl class="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4" data-testid="cash-forecast">
            <div>
              <dt class="text-muted-foreground">{t(msg`Collected in advance`)}</dt>
              <dd class="font-semibold">
                <Money
                  usdMinor={forecast.data?.prepaidCollectedUsdMinor ?? 0}
                  vesMinor={forecast.data?.prepaidCollectedVesMinor}
                  showRate={false}
                />
              </dd>
            </div>
            <div>
              <dt class="text-muted-foreground">{t(msg`Still projected`)}</dt>
              <dd class="font-semibold">
                <Money
                  usdMinor={forecast.data?.projectedUsdMinor ?? 0}
                  vesMinor={forecast.data?.projectedVesMinor}
                  showRate={false}
                />
              </dd>
            </div>
            <div>
              <dt class="text-muted-foreground">{t(msg`Overdue`)}</dt>
              <dd class="font-semibold text-red-600">
                <Money
                  usdMinor={forecast.data?.overdueUsdMinor ?? 0}
                  vesMinor={forecast.data?.overdueVesMinor}
                  showRate={false}
                />
              </dd>
            </div>
            <div>
              {/* Lo que cuesta adelantar la caja: el descuento concedido. */}
              <dt class="text-muted-foreground">{t(msg`Discount granted`)}</dt>
              <dd class="font-semibold">
                <Money
                  usdMinor={forecast.data?.prepayDiscountUsdMinor ?? 0}
                  vesMinor={forecast.data?.prepayDiscountVesMinor}
                  showRate={false}
                />
              </dd>
            </div>
          </dl>
        </Show>
      </div>

      {/* Estado y drenaje por pasarela. */}
      <div class="rounded-xl border border-border p-4">
        <h2 class="mb-1 flex items-center gap-2 font-semibold">
          <PlugZap class="h-4 w-4" />
          {t(msg`Payment gateways`)}
        </h2>
        <p class="mb-3 text-sm text-muted-foreground">
          {t(
            msg`Draining stops new checkouts but keeps settling what is already in flight — turning a gateway off never discards a webhook.`,
          )}
        </p>
        <Show when={!panel.isLoading} fallback={<Skeleton class="h-32 w-full" />}>
          <ul class="divide-y divide-border">
            <For each={panel.data}>
              {(g) => (
                <li class="py-3 text-sm" data-testid={`gateway-${g.key}`}>
                  <div class="flex flex-wrap items-center gap-2">
                    <span class="font-medium">{g.name}</span>
                    <span
                      class={`rounded-full px-2 py-0.5 text-xs ${stateClass(g.state)}`}
                      data-testid={`gateway-state-${g.key}`}
                    >
                      {g.state}
                    </span>
                    <Show when={g.inFlightCount > 0}>
                      {(() => {
                        const inFlight = g.inFlightCount;
                        return (
                          <span
                            class="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300"
                            data-testid={`gateway-inflight-${g.key}`}
                          >
                            <AlertTriangle class="h-3 w-3" />
                            {t(msg`${inFlight} in flight`)}
                            <Show when={g.oldestInFlightAt}>
                              {(at) => {
                                const mins = minutesAgo(at());
                                return <> · {t(msg`oldest ${mins} min ago`)}</>;
                              }}
                            </Show>
                          </span>
                        );
                      })()}
                    </Show>
                    <span class="ml-auto text-xs text-muted-foreground">
                      {t(msg`30 days`)}:{" "}
                      <Money usdMinor={g.collectedUsdMinor30d} showRate={false} />
                      <Show when={g.supports.feeReported && g.providerFeeUsdMinor30d > 0}>
                        {" · "}
                        {t(msg`fees`)}:{" "}
                        <Money usdMinor={g.providerFeeUsdMinor30d} showRate={false} />
                      </Show>
                    </span>
                  </div>
                  <div class="mt-2 flex flex-wrap items-center gap-2">
                    <For each={["active", "draining", "off"] as GatewayState[]}>
                      {(s) => (
                        <Button
                          size="sm"
                          variant={g.state === s ? "default" : "outline"}
                          disabled={setState.isPending || g.state === s}
                          data-testid={`gateway-${g.key}-${s}`}
                          onClick={() => {
                            onSetState(g.key, s);
                          }}
                        >
                          {s}
                        </Button>
                      )}
                    </For>
                    <For each={g.pendingEvents}>
                      {(p) => (
                        <span class="rounded-full bg-secondary/20 px-2 py-0.5 text-xs">
                          {p.count} {p.status}
                        </span>
                      )}
                    </For>
                  </div>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </div>

      {/* Cola de eventos: lo que espera a un humano. */}
      <div class="rounded-xl border border-border p-4">
        <h2 class="mb-1 font-semibold">{t(msg`Webhook queue`)}</h2>
        <p class="mb-3 text-sm text-muted-foreground">
          {t(
            msg`Every signed event is stored before it is applied. Nothing is ever discarded: what could not be applied waits here.`,
          )}
        </p>
        <Show when={!events.isLoading} fallback={<Skeleton class="h-20 w-full" />}>
          <Show
            when={(events.data?.length ?? 0) > 0}
            fallback={<p class="text-sm text-muted-foreground">{t(msg`Queue is empty`)}</p>}
          >
            <ul class="divide-y divide-border" data-testid="webhook-queue">
              <For each={events.data}>
                {(e) => (
                  <li class="py-2 text-sm" data-testid={`webhook-event-${e.id}`}>
                    <div class="flex flex-wrap items-center gap-2">
                      <span class="font-mono text-xs">{e.eventId}</span>
                      <span class="rounded-full bg-secondary/20 px-2 py-0.5 text-xs">
                        {e.provider} · {e.status}
                      </span>
                      <Show when={e.lastError}>
                        <span class="text-xs text-red-600">{e.lastError}</span>
                      </Show>
                      <Show when={e.status === "manual" || e.status === "failed"}>
                        <Button
                          size="sm"
                          variant="secondary"
                          class="ml-auto"
                          data-testid={`webhook-settle-${e.id}`}
                          onClick={() => {
                            setSettling(settling() === e.id ? undefined : e.id);
                          }}
                        >
                          {t(msg`Resolve by hand`)}
                        </Button>
                      </Show>
                    </div>
                    <Show when={settling() === e.id}>
                      <div class="mt-2 flex items-center gap-2">
                        <input
                          class="w-full rounded-lg border-2 border-border bg-field-background px-3 py-1.5 text-sm"
                          placeholder={t(msg`How was it resolved?`)}
                          value={note()}
                          onInput={(ev) => {
                            setNote(ev.currentTarget.value);
                          }}
                        />
                        <Button
                          size="sm"
                          onClick={() => {
                            onSettle(e.id);
                          }}
                        >
                          {t(msg`Confirm`)}
                        </Button>
                      </div>
                    </Show>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </Show>
      </div>
    </div>
  );
};
